/**
 * Registry of currently-mounted Guided Walkthrough targets, keyed by stepId
 * (see `walkthroughStepIds` in constants/featureAnnouncements.js).
 *
 * Module-level, not a Pinia store — mirrors the register/unregister idiom
 * already used by useModalStack.js. A component calls
 * `useWalkthroughTarget(stepId)` to register a real template ref on mount
 * and deregister it on unmount. The Walkthrough Controller calls
 * `getWalkthroughTarget(stepId)` to resolve whichever element is currently
 * registered for the active step — never `document.getElementById`.
 *
 * Bind the returned ref with the STATIC attribute form, matching the variable name:
 *
 *   const savedSearchesButtonTarget = useWalkthroughTarget(walkthroughStepIds.foo);
 *   <AcvButton ref="savedSearchesButtonTarget" />   // ✅ registers
 *   <AcvButton :ref="savedSearchesButtonTarget" />  // ❌ ref stays null
 *
 * The dynamic form does not populate the ref in this Vue/compiler setup, and the failure
 * is silent: nothing registers, so the Controller simply skips that step. Components
 * (not just elements) are fine — the Controller resolves an instance to its element.
 */
const targetRegistry = reactive(new Map());

/**
 * @param {string} stepId
 * @param {boolean | import('vue').Ref<boolean>} [isEnabled] Gates registration for a target
 * repeated across a `v-for` (e.g. one per job card) — only the currently-relevant instance may
 * register, or whichever mounted last would win the slot. Defaults to always-on. A ref, since
 * "which one" can change after every instance has already mounted.
 */
export const useWalkthroughTarget = (stepId, isEnabled = true) => {
  const target = ref(null);
  const enabledRef = isRef(isEnabled) ? isEnabled : ref(isEnabled);

  const register = (element) => {
    if (element && enabledRef.value) targetRegistry.set(stepId, element);
  };

  const unregister = (element) => {
    if (element && targetRegistry.get(stepId) === element) targetRegistry.delete(stepId);
  };

  /**
   * Watching the ref, not just registering on mount, because a target frequently appears
   * *after* its component does: the mobile saved-search list mounts while its fetch is still
   * in flight and only renders its buttons once the data lands. Mount-time registration alone
   * missed those entirely — and silently, since an unregistered step is simply skipped.
   */
  watch(target, (element, previous) => {
    unregister(previous);
    register(element);
  });

  watch(enabledRef, (enabled) => {
    if (enabled) {
      register(target.value);
    } else {
      unregister(target.value);
    }
  });

  onMounted(() => register(target.value));
  onBeforeUnmount(() => unregister(target.value));

  return target;
};

export const getWalkthroughTarget = (stepId) => computed(() => targetRegistry.get(stepId) ?? null);
